import clsx from 'clsx';

export interface UnlockFullArcanumAccessPromptProps {
  /** Optional copy override. Defaults to the canonical CTA prose. */
  caption?: string;
  /**
   * Where the "Unlock" button navigates. Defaults to `/signup`. Public-deal
   * mounts SHOULD pass `authUrlWithShare('/signup', shareId)` so post-auth
   * `linkSharesForUser` fires (a bare `/signup` drops the shareId).
   */
  signupHref?: string;
  /**
   * Optional analytics hook fired on Sign-Up click (before navigation) —
   * public-deal mounts pass a `deal_signup_cta_clicked` PostHog capture.
   */
  onSignupClick?: () => void;
  className?: string;
}

/**
 * `agent-ux-optimization` Phase 6 Session 6 (2026-05-08).
 *
 * Conversion CTA shown to public-deal viewers when they hit the soft
 * 3-question gate (`gateActive: true` from
 * `POST /api/shared/:shareId/questions`). Also rendered as a
 * persistent footer on the public chrome regardless of gate state —
 * the bottom-right "UNLOCK FULL ARCANUM ACCESS" CTA per blueprint.
 *
 * Pure presentation; no side effects. The href defaults to `/signup`
 * which routes anonymous visitors into the existing signup flow.
 */
export function UnlockFullArcanumAccessPrompt({
  caption = "You've reached the question limit on this deal. Sign up to keep exploring with full access to Arcanum.",
  signupHref = '/signup',
  onSignupClick,
  className,
}: UnlockFullArcanumAccessPromptProps) {
  return (
    <div
      data-testid="unlock-full-arcanum-access-prompt"
      className={clsx(
        'flex flex-col gap-3 px-5 py-4 border border-accent/30 rounded-[3px] bg-accent-dim',
        className
      )}
    >
      <h3 className="text-sm font-mono font-semibold tracking-[0.3px] uppercase text-accent">
        Unlock Full Arcanum Access
      </h3>
      <p className="text-xs font-mono leading-relaxed text-text">{caption}</p>
      <a
        href={signupHref}
        data-testid="unlock-cta"
        onClick={onSignupClick}
        className="self-start px-3 py-1.5 border border-accent/40 rounded-[2px] bg-accent/10 text-accent text-xs font-mono font-semibold tracking-[0.3px] uppercase hover:bg-accent/20 transition-colors no-underline"
      >
        Sign Up →
      </a>
    </div>
  );
}
